import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  HiArrowLeft, HiPencil, HiTrash,
  HiCheckCircle, HiPrinter, HiBell,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatAmount, formatDateLong } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  Paid:    'bg-green-100  text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Overdue: 'bg-red-100    text-red-600',
  Draft:   'bg-gray-100   text-gray-500',
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ invoice, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Invoice</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Delete <span className="font-semibold text-gray-800">{invoice.invoice_number}</span>?
          <br />This action cannot be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function InvoiceDetail() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const { id }    = useParams();

  const [invoice,      setInvoice]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [markingPaid,  setMarkingPaid]  = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [paying,          setPaying]          = useState(false);

  const fetchInvoice = useCallback(async () => {
    const { data, error } = await supabase
      .from('invoices')
      .select('*, clients(name, email, phone, company)')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (error || !data) {
      toast.error('Invoice not found');
      navigate('/invoices');
    } else {
      setInvoice(data);
    }
    setLoading(false);
  }, [id, user.id, navigate]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const markAsPaid = async () => {
    setMarkingPaid(true);
    const { error } = await supabase
      .from('invoices')
      .update({ status: 'Paid' })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Invoice marked as paid!');
      setInvoice((prev) => ({ ...prev, status: 'Paid' }));
    }
    setMarkingPaid(false);
  };

  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const res = await fetch('/api/email/invoice-reminder', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId:     invoice.id,
          clientEmail:   client.email,
          clientName:    client.name,
          invoiceNumber: invoice.invoice_number,
          amount:        formatAmount(total),
          dueDate:       invoice.due_date,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      toast.success('Reminder sent to ' + client.email);
      const now = new Date().toISOString();
      setInvoice((prev) => ({ ...prev, reminder_sent_at: now }));
    } catch (err) {
      toast.error('Failed to send reminder: ' + err.message);
    }
    setSendingReminder(false);
  };

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await fetch('/api/payments/create-checkout-session', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId:        invoice.id,
          amount:           total,
          clientEmail:      client.email,
          invoiceNumber:    invoice.invoice_number,
          freelancerUserId: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create session');
      window.location.href = data.sessionUrl;
    } catch (err) {
      toast.error('Payment error: ' + err.message);
      setPaying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Invoice deleted');
      navigate('/invoices');
    }
    setDeleting(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout title="Invoice">
        <div className="flex items-center justify-center py-32">
          <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      </Layout>
    );
  }

  if (!invoice) return null;

  const items = invoice.items ?? [];
  const total = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
    0
  );
  const client = invoice.clients ?? {};

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title={invoice.invoice_number}>

      {/* Action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            <HiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{invoice.invoice_number}</h2>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[invoice.status]}`}>
                {invoice.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {client.name} · Due {formatDateLong(invoice.due_date)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium
              text-gray-600 hover:bg-gray-100 rounded-xl transition border border-gray-200"
          >
            <HiPrinter className="w-4 h-4" /> Print
          </button>
          <button
            onClick={() => navigate(`/invoices/${id}/edit`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium
              text-gray-600 hover:bg-gray-100 rounded-xl transition border border-gray-200"
          >
            <HiPencil className="w-4 h-4" /> Edit
          </button>
          {(invoice.status === 'Pending' || invoice.status === 'Overdue') && (
            <button
              onClick={handlePayNow}
              disabled={paying}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold
                bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition disabled:opacity-60 shadow-sm"
            >
              {paying ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Redirecting…
                </>
              ) : '💳 Pay Now'}
            </button>
          )}
          {invoice.status !== 'Paid' && (
            <button
              onClick={markAsPaid}
              disabled={markingPaid}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold
                bg-green-600 hover:bg-green-700 text-white rounded-xl transition disabled:opacity-60 shadow-sm"
            >
              <HiCheckCircle className="w-4 h-4" />
              {markingPaid ? 'Updating…' : 'Mark as Paid'}
            </button>
          )}
          {invoice.status !== 'Paid' && client.email && (
            <button
              onClick={sendReminder}
              disabled={sendingReminder}
              title={invoice.reminder_sent_at ? `Last sent ${formatDateLong(invoice.reminder_sent_at)}` : 'Send payment reminder'}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium
                text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl transition border border-amber-200
                disabled:opacity-60"
            >
              <HiBell className="w-4 h-4" />
              {sendingReminder ? 'Sending…' : invoice.reminder_sent_at ? 'Resend Reminder' : 'Send Reminder'}
            </button>
          )}
          <button
            onClick={() => setShowDelete(true)}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
            title="Delete invoice"
          >
            <HiTrash className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Invoice document ── */}
      <div
        id="invoice-print"
        className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden
          print:shadow-none print:border-none print:rounded-none"
      >
        {/* Indigo header band */}
        <div className="bg-indigo-600 px-8 py-7 flex flex-col sm:flex-row justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-base">P</span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight">PortalFlow</span>
            </div>
            <p className="text-indigo-300 text-xs">Client Portal &amp; Invoice Manager</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">Invoice</p>
            <p className="text-white font-bold text-3xl tracking-tight">{invoice.invoice_number}</p>
          </div>
        </div>

        {/* Document body */}
        <div className="px-8 py-8">

          {/* Bill-to + dates */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 pb-8 border-b border-gray-100">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Bill To</p>
              <p className="text-sm font-bold text-gray-900">{client.name}</p>
              {client.company && <p className="text-sm text-gray-500 mt-0.5">{client.company}</p>}
              {client.email   && <p className="text-sm text-gray-500 mt-0.5">{client.email}</p>}
              {client.phone   && <p className="text-sm text-gray-500 mt-0.5">{client.phone}</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Invoice Date</p>
              <p className="text-sm font-medium text-gray-900">{formatDateLong(invoice.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Due Date</p>
              <p className="text-sm font-medium text-gray-900">{formatDateLong(invoice.due_date)}</p>
              <p className={`text-xs font-semibold mt-1.5 inline-flex px-2 py-0.5 rounded-full
                ${STATUS_STYLE[invoice.status]}`}>
                {invoice.status}
              </p>
              {invoice.reminder_sent_at && (
                <p className="text-xs text-amber-600 mt-1.5">
                  Reminder sent {formatDateLong(invoice.reminder_sent_at)}
                </p>
              )}
            </div>
          </div>

          {/* Line items table */}
          <div className="rounded-xl border border-gray-100 overflow-hidden mb-8">
            {/* Table header */}
            <div className="grid grid-cols-[minmax(0,1fr)_72px_100px_100px] gap-4 px-5 py-3
              bg-gray-50 border-b border-gray-100">
              {['Description', 'Qty', 'Unit Price', 'Amount'].map((h, i) => (
                <p
                  key={h}
                  className={`text-xs font-semibold text-gray-500 uppercase tracking-wider
                    ${i === 3 ? 'text-right' : ''}`}
                >
                  {h}
                </p>
              ))}
            </div>

            {/* Rows */}
            {items.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">No line items</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[minmax(0,1fr)_72px_100px_100px] gap-4 px-5 py-3.5">
                    <p className="text-sm text-gray-900">{item.description}</p>
                    <p className="text-sm text-gray-500 text-center">{item.quantity}</p>
                    <p className="text-sm text-gray-500">{formatAmount(item.price)}</p>
                    <p className="text-sm font-semibold text-gray-900 text-right">
                      {formatAmount(Number(item.quantity) * Number(item.price))}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals block */}
          <div className="flex justify-end">
            <div className="w-60 space-y-2">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span>{formatAmount(total)}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span className="text-indigo-600">{formatAmount(total)}</span>
              </div>
              {invoice.status === 'Paid' && (
                <div className="flex justify-between text-sm text-green-600 font-semibold pt-1">
                  <span>Amount Paid</span>
                  <span>{formatAmount(total)}</span>
                </div>
              )}
              {invoice.status === 'Pending' && (
                <div className="flex justify-between text-sm text-yellow-600 font-semibold pt-1">
                  <span>Amount Due</span>
                  <span>{formatAmount(total)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Notes</p>
              <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Generated by <span className="font-semibold text-gray-500">PortalFlow</span> · Thank you for your business!
            </p>
          </div>
        </div>
      </div>

      {showDelete && (
        <DeleteModal
          invoice={invoice}
          onClose={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </Layout>
  );
}
